import { useMemo } from 'react';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { hydraUiApi, useHydraUIAPIQuery } from '@/services/api';
import { isEmptyString } from '@/utils/string';
import { isRecord } from '@/utils/objects';
import type {
  ActivityLog,
  ActivityLogCustomer,
  ActivityLogFilters,
  ActivityLogObject,
  ActivityLogRow,
  ActivityLogSegment,
  ActivityLogTab,
} from './types';

interface SendEventVars {
  environmentId: string;
  /** Internal CIO id of the recipient profile. */
  cioId: string;
  name: string;
  /** JSON-encoded event attributes ("{}" when none). */
  data: string;
}

/** Send (resend) a test event to a profile — `POST /v1/environments/:id/events`. */
export function useSendEvent() {
  return useMutation({
    mutationFn: ({ environmentId, cioId, name, data }: SendEventVars) =>
      hydraUiApi.events.create({
        params: { path: { environment_id: environmentId } },
        body: { ids: [cioId], name, data },
      }),
  });
}

/**
 * Resolve the segment a log refers to from the sideloaded `segments`: segment-build logs carry the
 * id in `name`; membership-change logs reference it via `attrs.entered` / `attrs.exited` (only when
 * exactly one is referenced).
 */
/** segment-build logs carry the segment id in `name`. */
function segmentBuildSegment(
  log: ActivityLog,
  segmentById: Map<number, ActivityLogSegment>,
): ActivityLogSegment | undefined {
  const id = Number(log.name);
  return Number.isNaN(id) ? undefined : segmentById.get(id);
}

/** membership-change logs reference a segment via `attrs.entered`/`attrs.exited` (only when exactly one). */
function membershipChangeSegment(
  log: ActivityLog,
  segmentById: Map<number, ActivityLogSegment>,
): ActivityLogSegment | undefined {
  if (!isRecord(log.attrs)) return undefined;
  const record = log.attrs;
  const entered = Array.isArray(record.entered) ? record.entered : [];
  const exited = Array.isArray(record.exited) ? record.exited : [];
  const ids = [...entered, ...exited];
  return ids.length === 1 ? segmentById.get(Number(ids[0])) : undefined;
}

function resolveLogSegment(
  log: ActivityLog,
  segmentById: Map<number, ActivityLogSegment>,
): ActivityLogSegment | undefined {
  if (log.type === 'segment_build') return segmentBuildSegment(log, segmentById);
  if (log.type === 'profile_segment_membership_change') return membershipChangeSegment(log, segmentById);
  return undefined;
}

/** Identified-customer logs are capped at 50 per page server-side; match it for both tabs. */
const PAGE_SIZE = 50;

/** Auto-refresh cadence (10s). */
export const ACTIVITY_LOGS_REFRESH_INTERVAL_MS = 10_000;

// The generated client types the response as a union (JSON body | raw Blob/Response/…); narrow to
// the JSON page shape we actually consume by the presence of `logs`.
type LogsPage = Extract<Awaited<ReturnType<typeof hydraUiApi.logs.list>>, { logs: unknown }>;

/** Key a customer by both its external and internal id (a log may reference either). */
function indexCustomer(customerById: Map<string, ActivityLogCustomer>, customer: ActivityLogCustomer) {
  if (!isEmptyString(customer.id)) customerById.set(customer.id, customer);
  if (!isEmptyString(customer.internal_id)) customerById.set(customer.internal_id, customer);
}

/**
 * Build id → record lookups from every page's sideloaded relations. The generated type marks
 * `customers` non-optional, but the anonymous endpoint returns `null` for it at runtime — the
 * `?? []` guards against that spec/runtime mismatch.
 */
function buildRelationLookups(pages: LogsPage[]) {
  const customerById = new Map<string, ActivityLogCustomer>();
  const objectById = new Map<string, ActivityLogObject>();
  const segmentById = new Map<number, ActivityLogSegment>();
  for (const page of pages) {
    for (const customer of page.customers ?? []) indexCustomer(customerById, customer);
    for (const object of page.objects ?? []) objectById.set(object.id, object);
    for (const segment of page.segments ?? []) segmentById.set(segment.id, segment);
  }
  return { customerById, objectById, segmentById };
}

/** Join each page's logs to their sideloaded customer/object/segment records. */
function buildLogRows(pages: LogsPage[], lookups: ReturnType<typeof buildRelationLookups>): ActivityLogRow[] {
  const { customerById, objectById, segmentById } = lookups;
  return pages.flatMap((page) =>
    (page.logs ?? []).map((log) => ({
      log,
      customer: customerById.get(log.customer_id ?? log.internal_id ?? ''),
      object: isEmptyString(log.object_id ?? '') ? undefined : objectById.get(log.object_id ?? ''),
      segment: resolveLogSegment(log, segmentById),
    })),
  );
}

interface UseActivityLogsOptions {
  /** Workspace id from the route == the API `environment_id`. */
  environmentId: string;
  tab: ActivityLogTab;
  filters: ActivityLogFilters;
  /** When true, poll every 10s (the page owns the toggle + `save_auto_refresh` user flag). */
  autoRefresh: boolean;
}

function blankToUndefined(value: string): string | undefined {
  return isEmptyString(value) ? undefined : value;
}

/**
 * Workspace `sent_events` / `sent_attributes` — the options the Activity Type name filter offers.
 * Also exposes the workspace's `identifiers` config (e.g. `{ email, id, cio_id }` → numeric state;
 * `0` = disabled) so the Activity Name label can count how many changed attributes are identifiers,
 * and whether the custom SMS action is enabled (drives the twilio/sms Activity Type swap).
 * Cached; scoped to the workspace.
 */
export function useActivityLogEnvironment(environmentId: string) {
  const query = useHydraUIAPIQuery({
    queryKey: ['activityLogEnvironment', environmentId],
    queryFn: ({ signal }) =>
      hydraUiApi.environments.get({ params: { path: { environment_id: environmentId } }, signal }),
    queryConfig: { enabled: !isEmptyString(environmentId), staleTime: 60_000 },
  });
  return {
    sentEvents: query.data?.environment?.sent_events ?? [],
    sentAttributes: query.data?.environment?.sent_attributes ?? [],
    identifiers: (query.data?.environment?.identifiers ?? {}) as Record<string, number>,
    customSmsEnabled: query.data?.environment?.enabled_actions?.includes('sms') ?? false,
  };
}

/** A webhook campaign for the Activity Log name filter — `id` is the value sent as the `name` filter. */
export type WebhookCampaignOption = { id: string; name: string };

/**
 * Webhook-triggered campaigns for the Activity Log's webhook name filter, searched server-side. For
 * `webhook_event` the logs API's `name` filter matches the campaign id (not a free-text name), so
 * this picker resolves a campaign name → its id. The campaigns list endpoint pages at 20 with no
 * size override, so the whole set can't be loaded for a client-side filter — instead each search
 * term (debounced by the caller) refetches with `search`. `type: 'webhook'` restricts to
 * webhook-triggered campaigns; metrics/actions/tags are excluded to keep the response small.
 * Cached per (workspace, search term).
 */
export function useWebhookCampaigns(environmentId: string, search: string) {
  const query = useHydraUIAPIQuery({
    queryKey: ['activityLogWebhookCampaigns', environmentId, search],
    queryFn: ({ signal }) =>
      hydraUiApi.campaigns.list({
        params: {
          path: { environment_id: environmentId },
          query: {
            type: 'webhook',
            search: isEmptyString(search) ? undefined : search,
            excludeActions: true,
            excludeMetrics: true,
            excludeTags: true,
            excludeSegmentMatchCount: true,
          },
        },
        signal,
      }),
    queryConfig: { enabled: !isEmptyString(environmentId), staleTime: 60_000 },
  });
  const campaigns: WebhookCampaignOption[] = (query.data?.campaigns ?? []).map((campaign) => ({
    id: String(campaign.id),
    name: campaign.name,
  }));
  return { campaigns, isLoading: query.isLoading };
}

/**
 * Resolve a single campaign's name by id — seeds the webhook name filter's input when the filter is
 * populated from a deep link (`?type=webhook_event&name=<campaignId>`), where only the id is known.
 * Cached by id (shares the key used elsewhere for campaign lookups); only fetches when an id is set.
 */
export function useCampaignName(environmentId: string, campaignId: string): string | undefined {
  const cid = isEmptyString(campaignId) ? '' : campaignId;
  const query = useHydraUIAPIQuery({
    queryKey: ['activityLogCampaign', environmentId, cid],
    queryFn: ({ signal }) =>
      hydraUiApi.campaigns.get({ params: { path: { environment_id: environmentId, campaign_id: cid } }, signal }),
    queryConfig: { enabled: !isEmptyString(cid) && !isEmptyString(environmentId), staleTime: 60_000 },
  });
  return query.data?.campaign?.name;
}

/**
 * Workspace forms (custom + Facebook lead-ads) for resolving `form_integration` log sources to a
 * named form link. Lazy — only fetched when a form-integration source actually renders. Cached;
 * scoped to the workspace.
 */
export function useActivityLogForms(environmentId: string, enabled: boolean) {
  const on = enabled && !isEmptyString(environmentId);
  const custom = useHydraUIAPIQuery({
    queryKey: ['activityLogCustomForms', environmentId],
    queryFn: ({ signal }) =>
      hydraUiApi.customForms.list({ params: { path: { environment_id: environmentId } }, signal }),
    queryConfig: { enabled: on, staleTime: 60_000 },
  });
  const facebook = useHydraUIAPIQuery({
    queryKey: ['activityLogFacebookForms', environmentId],
    queryFn: ({ signal }) =>
      hydraUiApi.facebookLeadAds.getFacebookLeadAdsForms({
        params: { path: { environment_id: environmentId } },
        signal,
      }),
    queryConfig: { enabled: on, staleTime: 60_000 },
  });
  return {
    customForms: custom.data?.custom_forms ?? [],
    facebookForms: facebook.data?.facebook_lead_ads_forms ?? [],
    isPending: on && (custom.isLoading || facebook.isLoading),
  };
}

/** The campaign / broadcast an in-app form belongs to, for the form-submissions deep link. */
export type InAppFormParent = { name: string; kind: 'campaign' | 'newsletter'; id: string };

/**
 * Resolves an in-app form's parent campaign/broadcast for the `form_integration` (in-app) source
 * via a dependent-fetch chain: template → its action → that action's campaign, OR template → its
 * newsletter. Each step is a React Query enabled on the prior id (cached + deduped across rows).
 * Lazy: nothing fetches without a `templateId`.
 */
export function useInAppFormParent(environmentId: string, templateId: string | number | null | undefined) {
  const tid = templateId == null ? '' : String(templateId);
  const enabled = !isEmptyString(tid) && !isEmptyString(environmentId);

  const template = useHydraUIAPIQuery({
    queryKey: ['activityLogTemplate', environmentId, tid],
    queryFn: ({ signal }) =>
      hydraUiApi.templates.get({ params: { path: { environment_id: environmentId, template_id: tid } }, signal }),
    queryConfig: { enabled, staleTime: 60_000 },
  });
  const aid = template.data?.template?.action_id == null ? '' : String(template.data.template.action_id);
  const nid = template.data?.template?.newsletter_id == null ? '' : String(template.data.template.newsletter_id);

  const action = useHydraUIAPIQuery({
    queryKey: ['activityLogAction', environmentId, aid],
    queryFn: ({ signal }) =>
      hydraUiApi.actions.get({ params: { path: { environment_id: environmentId, action_id: aid } }, signal }),
    queryConfig: { enabled: !isEmptyString(aid), staleTime: 60_000 },
  });
  const cid = action.data?.action?.campaign_id == null ? '' : String(action.data.action.campaign_id);

  const campaign = useHydraUIAPIQuery({
    queryKey: ['activityLogCampaign', environmentId, cid],
    queryFn: ({ signal }) =>
      hydraUiApi.campaigns.get({ params: { path: { environment_id: environmentId, campaign_id: cid } }, signal }),
    queryConfig: { enabled: !isEmptyString(cid), staleTime: 60_000 },
  });
  const newsletter = useHydraUIAPIQuery({
    queryKey: ['activityLogNewsletter', environmentId, nid],
    queryFn: ({ signal }) =>
      hydraUiApi.newsletters.get({ params: { path: { environment_id: environmentId, id: nid } }, signal }),
    queryConfig: { enabled: !isEmptyString(nid), staleTime: 60_000 },
  });

  let parent: InAppFormParent | null = null;
  if (!isEmptyString(cid) && campaign.data?.campaign != null) {
    parent = { name: campaign.data.campaign.name, kind: 'campaign', id: cid };
  } else if (!isEmptyString(nid) && newsletter.data?.newsletter != null) {
    parent = { name: newsletter.data.newsletter.name, kind: 'newsletter', id: nid };
  }
  const isLoading = enabled && (template.isLoading || action.isLoading || campaign.isLoading || newsletter.isLoading);
  return { parent, isLoading, hasTemplate: enabled };
}

/** Minimal SQL-import shape the source label needs (from `GET …/sql_imports/:id`). */
export type ActivityLogSqlImport = { id?: number; name?: string; database_type?: string };

/**
 * Lazily fetch a SQL import by id for the `sql_import` log source, via the typed client
 * (`hydraUiApi.sqlImports.get`). The generated response type is loose, so the result is narrowed
 * below. Cached by id; only fetched when a SQL-import source renders.
 */
export function useActivityLogSqlImport(environmentId: string, importId: string | number | null | undefined) {
  const iid = importId == null ? '' : String(importId);
  const enabled = !isEmptyString(iid) && !isEmptyString(environmentId);
  const query = useHydraUIAPIQuery({
    queryKey: ['activityLogSqlImport', environmentId, iid],
    queryFn: ({ signal }) =>
      hydraUiApi.sqlImports.get({ params: { path: { environment_id: environmentId, id: iid } }, signal }),
    queryConfig: { enabled, staleTime: 60_000 },
  });
  const data = query.data as ({ sql_import?: ActivityLogSqlImport } & ActivityLogSqlImport) | undefined;
  return { sqlImport: data?.sql_import ?? data ?? null, isPending: enabled && query.isLoading };
}

/**
 * Lazily fetch a single delivery by id — a per-row load, kept lazy on purpose. React Query caches
 * by id, so repeated/adjacent rows that reference the same delivery resolve without a new request.
 * Disabled when there's no id.
 */
export function useActivityLogDelivery(environmentId: string, deliveryId: string | null | undefined) {
  const enabled = !isEmptyString(deliveryId ?? '');
  const query = useHydraUIAPIQuery({
    queryKey: ['activityLogDelivery', environmentId, deliveryId ?? ''],
    queryFn: ({ signal }) =>
      hydraUiApi.deliveries.get({ params: { path: { environment_id: environmentId, id: deliveryId ?? '' } }, signal }),
    queryConfig: { enabled },
  });
  return {
    subject: query.data?.delivery?.subject,
    isPending: enabled && query.isLoading,
    /** Delivery id was set but the record is gone (deleted) — the caller falls through to the raw name. */
    notFound: enabled && !query.isLoading && query.data?.delivery == null,
  };
}

/**
 * Activity-logs data hook over `GET /v1/environments/{id}/logs`.
 *
 * Cursor pagination via `meta.continuation` ("load more"); the reply sideloads `customers` /
 * `objects`, which we join by id into row view-models so the cells never fetch per-row.
 * Auto-refresh maps to React Query's `refetchInterval`, which refetches all loaded pages (so
 * accumulated "load more" results stay fresh rather than resetting to page 1).
 */
export function useActivityLogs({ environmentId, tab, filters, autoRefresh }: UseActivityLogsOptions) {
  const isAnonymous = tab === 'anonymous';

  const query = useInfiniteQuery({
    queryKey: ['activityLogs', environmentId, tab, filters],
    initialPageParam: '',
    queryFn: ({ pageParam, signal }) =>
      hydraUiApi.logs.list({
        params: {
          path: { environment_id: environmentId },
          query: {
            anonymous: isAnonymous ? true : undefined,
            type: blankToUndefined(filters.type),
            name: blankToUndefined(filters.name),
            from: blankToUndefined(filters.from),
            to: blankToUndefined(filters.to),
            limit: PAGE_SIZE,
            continuation: isEmptyString(pageParam) ? undefined : pageParam,
          },
        },
        signal,
      }),
    getNextPageParam: (lastPage) =>
      isEmptyString(lastPage.meta.continuation) ? undefined : lastPage.meta.continuation,
    refetchInterval: autoRefresh ? ACTIVITY_LOGS_REFRESH_INTERVAL_MS : false,
    enabled: !isEmptyString(environmentId),
  });

  const { rows, segmentById } = useMemo(() => {
    const pages = query.data?.pages ?? [];
    const lookups = buildRelationLookups(pages);
    // `segmentById` is also returned so the expanded-row JSON formatter can resolve the
    // segment ids in `entered`/`exited` arrays to name links.
    return { rows: buildLogRows(pages, lookups), segmentById: lookups.segmentById };
  }, [query.data]);

  return {
    rows,
    segmentById,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    hasMore: query.hasNextPage,
    fetchMore: query.fetchNextPage,
    isFetchingMore: query.isFetchingNextPage,
    refetch: query.refetch,
    /** ms epoch of the last successful fetch — feeds the "Last updated …" label. */
    lastUpdated: query.dataUpdatedAt,
  };
}
